import React, { useRef, useState } from 'react';
import { MenuButton } from '../../helperComponents/MenuButton';

import { UniversalProps } from '../../../typesInterfacesEnums/componentProps';
import { ButtonRowDivider, mainLobbyButtonSharedProps } from './utils';
import { Divider } from '../../helperComponents/Divider';
import { View, StyleSheet, useWindowDimensions } from 'react-native';
import { HowToPlay } from '../../helperComponents/HowToPlay';
import HowToPlayButton from '../../HowToPlayButton';
import { useRoute } from '@react-navigation/native';
import { useSignedInContext } from '../../../context/SignedInContext';
import { USER_TYPES } from '../../../typesInterfacesEnums/enums';
import { TooltipSequence } from '../../helperComponents/ToolTip/ToolTipSequence';
import { useGetHivesQuery } from '../MyHives/useGetHivesQuery.gql';
import { isMobileApp } from '@mightybyte/rnw.utils.device-info';
import { useOpenClose } from '../../../utils/hooks';
import { SelectHivePopUp } from '../../helperComponents/SelectHivePopUp';
import { HiveSize } from '../../../constants/constants';

const bee1Icon = require('../../../resources/images/bee1.png');
const bee2Icon = require('../../../resources/images/bee2.png');
const lockIcon = require('../../../resources/images/lock.png');

export default function MainLobby({
  navigation,
  playingName,
}: {
  navigation: UniversalProps['navigation'];
  playingName: string | undefined;
}) {
  const [isHowToPlayOpen, setHowToPlayOpen] = useState(false);
  const route = useRoute();

  const { user } = useSignedInContext();
  const { error: getHivesError, data: getHivesData, refetch } = useGetHivesQuery();
  const [isSelectHiveVisible, showSelectHive, hideSelectHive] = useOpenClose(false);
  const [path, setPath] = useState<'journey' | 'gym' | null>(null);

  const teacherOrAdmin =
    user?.userType === USER_TYPES.teacher || user?.userType === USER_TYPES.admin;

  const handleMyHivesPress = async () => {
    navigation.navigate('MyHives');
  };

  const noHives = !getHivesData || getHivesData.hives.length === 0;

  const handleHiveGymPress = () => {
    if (teacherOrAdmin && noHives) {
      // if mobile app do nothing
      if (isMobileApp) {
        return;
      }

      navigation.navigate('SelectHiveSize');
      return;
    }
    if (teacherOrAdmin) {
      navigation.navigate('Lobby', { gym: true });
    }
    setPath('gym');
    showSelectHive();
    // navigation.navigate('Lobby', { gym: true });
  };

  const handleJourneyPress = () => {
    if (!teacherOrAdmin && noHives) {
      navigation.navigate('MyHives', { shouldRefetch: true });
    } else {
      setPath('journey');
      showSelectHive();
    }
  };

  const handleSelectHiveJourney = (hiveDetail: { name: string; id: string; size: HiveSize }) => {
    hideSelectHive();
    setTimeout(() => {
      const currentParams = route.params || {};
      navigation.navigate('SelectGameType', {
        ...currentParams,
        journey: true,
        next: 'PlayWithAI',
        userName: playingName,
        hiveId: hiveDetail.id,
      });
    }, 300);
  };

  const handleSelectHiveGym = (hiveDetail: { name: string; id: string; size: HiveSize }) => {
    hideSelectHive();
    setTimeout(() => {
      const currentParams = route.params || {};
      navigation.navigate('Lobby', { gym: true, hiveId: hiveDetail.id });
    }, 300);
  };

  // 🧩 refs for tooltip targets
  const gymRef = useRef<View>(null);
  const howToPlayRef = useRef<View>(null);
  const { width } = useWindowDimensions();
  const pathSizeLimit = width < 1251;

  return (
    <>
      {user?.userType === USER_TYPES.student && (
        <View style={{ position: 'relative' }}>
          <MenuButton
            onPress={handleJourneyPress}
            colors={['#FFCF53', '#FFCF53']}
            text="My Learning Journeys"
            image={bee2Icon}
            {...mainLobbyButtonSharedProps}
          />
        </View>
      )}

      <ButtonRowDivider />

      {teacherOrAdmin && (
        <>
          <MenuButton
            onPress={handleMyHivesPress}
            colors={['#FFCF53', '#FFCF53']}
            text="My Hives"
            image={bee2Icon}
            {...mainLobbyButtonSharedProps}
          />
          <ButtonRowDivider />
        </>
      )}

      <View ref={gymRef} style={{ position: 'relative' }}>
        <MenuButton
          onPress={handleHiveGymPress}
          colors={['#747474', '#1F1F1F']}
          text="Hive Gym"
          disabled={(noHives && isMobileApp) || (!teacherOrAdmin && noHives && !isMobileApp)}
          image={
            (noHives && isMobileApp) || (!teacherOrAdmin && noHives && !isMobileApp)
              ? lockIcon
              : bee1Icon
          }
          {...mainLobbyButtonSharedProps}
        />
        <TooltipSequence
          stage={3}
          backgroundColor="rgba(227, 234, 29, 0.25)"
          borderColor="rgba(255, 255, 255, 0.4)"
          borderWidth={5}
        />
      </View>

      <ButtonRowDivider />
      <Divider empty height={30} />
      <View ref={howToPlayRef} style={{ position: 'relative' }}>
        <HowToPlayButton onPress={() => setHowToPlayOpen(true)} />
        <TooltipSequence
          stage={1}
          backgroundColor="rgba(227, 234, 29, 0.25)"
          borderColor="rgba(255, 255, 255, 0.4)"
          borderWidth={5}
        />
      </View>

      <HowToPlay visible={isHowToPlayOpen} setVisible={setHowToPlayOpen} />
      {pathSizeLimit && (
        <View style={{ position: 'relative' }}>
          <TooltipSequence
            stage={2}
            backgroundColor="rgba(240, 236, 7, 0.25)"
            borderColor="rgba(255, 255, 255, 0.4)"
            borderWidth={5}
          />
        </View>
      )}
      {isSelectHiveVisible && (
        <SelectHivePopUp
          isVisible={isSelectHiveVisible}
          onDismiss={hideSelectHive}
          onSelectHive={path === 'journey' ? handleSelectHiveJourney : handleSelectHiveGym}
        />
      )}
    </>
  );
}

const styles = StyleSheet.create({
  howToPlayText: {
    fontFamily: 'Secular One',
    fontSize: 20,
    color: 'black',
    textDecorationLine: 'underline',
  },
});
